// packages
import { format, getDay, parse, startOfWeek } from 'date-fns';
import { nl } from 'date-fns/locale/nl';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import { memo, useMemo } from 'react';
import {
  Calendar as ReactBigCalender,
  dateFnsLocalizer
} from 'react-big-calendar';

// styles
import { CalendarContainer } from './Calendar.styles';

// utils
import { CalendarDnDKitDefaults } from 'components/mrp/mrp.consts';
import { planningPeriodToBigCalendarPeriod } from './Calendar.utils';

// components
import CalendarColumnHeader from './components/CalendarColumnHeader/CalendarColumnHeader';

// types
import type { JSX } from 'react';
import type {
  DateLocalizer,
  Formats,
  Event as ReactBigCalendarEvent
} from 'react-big-calendar';
import type { CalendarProps, CalendarResource } from './Calendar.types';

const localizer: DateLocalizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: {
    nl
  }
});

const formats: Formats = {
  dateFormat: 'dd',
  timeGutterFormat: 'HH:mm',
  dayFormat: (date, culture, localizer) =>
    localizer!.format(date, 'EEEE dd MMM', culture)
};

const Calendar = <AppointmentEvent extends ReactBigCalendarEvent>({
  className,
  components,
  events = [],
  backgroundEvents = [],
  onDoubleClick,
  onSelectEvent,
  onNavigate,
  onSelectSlot,
  ref,
  selectable = true,
  periodType = 'week',
  showToolBar = false,
  startDate: startDateProp,
  scrollToTime,
  timeSlotMinMax: timeSlotMinMaxProp,
  timeStep = 30,
  eventPropGetter,
  resources,
  availabilityColumnWidths = []
}: CalendarProps<AppointmentEvent>) => {
  'use memo';

  // useMemo is required for startDate and timeSlotMinMax even with the React Compiler ('use memo').
  // new Date() and dayjs().toDate() are impure (time-dependent), so the compiler
  // cannot memoize them automatically. Without useMemo, a new object would be
  // created on every render when the prop is absent, causing unnecessary re-renders.
  const startDate = useMemo(() => startDateProp ?? new Date(), [startDateProp]);
  const timeSlotMinMax = useMemo(
    () =>
      timeSlotMinMaxProp ?? [
        dayjs().startOf('day').toDate(),
        dayjs().endOf('day').toDate()
      ],
    [timeSlotMinMaxProp]
  );

  // 'view' and 'views' must always be in sync: 'views' declares which views are available
  // and 'view' sets the active one. React Big Calendar throws if they disagree.
  const activePeriod = planningPeriodToBigCalendarPeriod(periodType);
  const views = {
    week: false,
    work_week: false,
    day: false,
    [activePeriod]: true
  };

  return (
    <CalendarContainer ref={ref} columnWidths={availabilityColumnWidths}>
      <ReactBigCalender<AppointmentEvent, CalendarResource>
        {...CalendarDnDKitDefaults}
        {...(!!resources && {
          resources,
          resourceGroupingLayout: true,
          resourceIdAccessor: ({ resourceId }) => resourceId,
          resourceTitleAccessor: ({ resourceTitle }) => resourceTitle
        })}
        className={className}
        components={{
          work_week: { header: CalendarColumnHeader },
          week: { header: CalendarColumnHeader },
          day: { header: CalendarColumnHeader },
          ...components
        }}
        culture="nl"
        date={new Date(startDate)}
        dayLayoutAlgorithm="no-overlap"
        showMultiDayTimes
        events={events}
        backgroundEvents={backgroundEvents}
        formats={formats}
        localizer={localizer}
        scrollToTime={scrollToTime}
        min={timeSlotMinMax[0]}
        max={timeSlotMinMax[1]}
        onDoubleClickEvent={onDoubleClick}
        onSelectEvent={onSelectEvent}
        onNavigate={onNavigate}
        onSelectSlot={onSelectSlot}
        selectable={selectable}
        startAccessor="start"
        tooltipAccessor={() => ''} // disable default ReactBigCalender tooltip
        timeslots={1}
        step={timeStep}
        endAccessor="end"
        toolbar={showToolBar}
        view={activePeriod}
        views={views}
        eventPropGetter={eventPropGetter}
      />
    </CalendarContainer>
  );
};

// This method reduces the entire object to only those relevant
// for memoization. Add any new properties that require the
// calendar to re-render here.
const reduceToMemoProps = (p: CalendarProps<any>) => {
  const keys: ReadonlyArray<Extract<keyof CalendarProps<any>, string>> = [
    'endDate',
    'events',
    'backgroundEvents',
    'selectable',
    'start',
    'periodType',
    'showToolBar',
    'startDate',
    'timeSlotMinMax',
    'timeStep',
    'availabilityColumnWidths',
    'className'
  ];

  return keys.reduce((acc, key) => {
    acc[key] = p[key];
    return acc;
  }, {});
};

const Memoed = memo(
  Calendar as (p: CalendarProps<any>) => JSX.Element,
  (prev, next) => {
    return isEqual(reduceToMemoProps(prev), reduceToMemoProps(next));
  }
);

// Explicit version is necessary because memoization doesn't allow
// passing generic.
export const MemoCalendar = <AppointmentEvent extends ReactBigCalendarEvent>(
  props: CalendarProps<AppointmentEvent>
) => {
  'use memo';
  return <Memoed {...props} />;
};

// Only use this if you want a completely unmemoized version of the
// calendar.
export default Calendar;
